import {
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Business } from '../config';
import { sliceWorkflowColumnDefinitions } from 'src/common/components/driver-mapping-components/config/driver-mapping-table';
import { filteringProperties } from 'src/shadow-pnl-allocations/pages/configure-slice-allocation-methodology-page/utils/slice-driver-mapping-filter-properties';
import { ValueWithLabel } from 'src/common/components/ValueWithLabel';
import { Schedule } from 'src/common/types';
import { Config } from 'src/common/types/Config';
import { ServerSideTable } from 'src/common/components/server-side-table/ServerSideTable';
import { getNextPageDriverMappingsHandler } from 'src/common/components/driver-mapping-components/utils/driverMapping';
import { AllocationServiceApi } from 'src/api/AllocationServiceApi';
import QueryKey from 'src/api/QueryKey';

interface ReviewStepInterface {
  config: Config;
  schedule: Schedule;
  onEdit(step: number): void;
  driverMappingId: string;
}

export const ReviewStep: FC<ReviewStepInterface> = (props) => {
  const { t } = useTranslation();
  const colDefs = useMemo(() => sliceWorkflowColumnDefinitions(t), [t]);

  const { data: driverMappingMetadata } = useQuery({
    queryKey: [QueryKey.GetDriverMappingMetadata, props.driverMappingId],
    queryFn: () =>
      AllocationServiceApi.getDriverMappingMetadataV2({
        dmId: props.driverMappingId,
      }),
    enabled: Boolean(props.driverMappingId),
  });

  const getNextPageDriverMappingEntries = useCallback(
    getNextPageDriverMappingsHandler(props.driverMappingId),
    [props.driverMappingId],
  );

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <Button
                data-testid="allocation-configuration-step-edit"
                onClick={() => props.onEdit(0)}
              >
                {t('edit')}
              </Button>
            }
          >
            {t('allocation')}
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <ValueWithLabel label={t('name')}>{props.config.name}</ValueWithLabel>
          <ValueWithLabel label={t('description')}>
            {props.config.description}
          </ValueWithLabel>
          <ValueWithLabel label={t('methodology')}>
            {props.config.methodology?.value}
          </ValueWithLabel>
          <ValueWithLabel label={t('business')}>
            {props.config.business?.value}
          </ValueWithLabel>
          <ValueWithLabel label={t('region')}>
            {props.config.region?.value}
          </ValueWithLabel>
          <ValueWithLabel label={t('country')}>
            {props.config.country.map((option) => option.value).join(', ')}
          </ValueWithLabel>
          <ValueWithLabel label={t('scenario')}>
            {props.config.scenario?.value}
          </ValueWithLabel>
        </ColumnLayout>
      </Container>
      {props.config.business?.value === Business.AB && (
        <Container
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  data-testid="driver-mapping-step-edit"
                  onClick={() => props.onEdit(1)}
                >
                  {t('edit')}
                </Button>
              }
            >
              {t('driver_mappings')}
            </Header>
          }
        >
          <SpaceBetween size="s">
            <ColumnLayout variant="text-grid" columns={3}>
              <ValueWithLabel label={t('driver_mapping_name')}>
                {driverMappingMetadata?.driverMappingMetadata?.name || '-'}
              </ValueWithLabel>
            </ColumnLayout>
            <ExpandableSection headerText={t('additional_driver_mappings')}>
              <ServerSideTable
                key={props.driverMappingId}
                driverMappingId={props.driverMappingId}
                columnDefinitions={colDefs}
                header={t('driver_mappings')}
                filteringProperties={filteringProperties(t)}
                testId="review-driver-mapping-table"
                fetchNextPage={getNextPageDriverMappingEntries}
                scenario={props.config.scenario?.value || ''}
                readOnly={true}
              />
            </ExpandableSection>
          </SpaceBetween>
        </Container>
      )}
      {props.config.business?.value !== Business.AB && (
        <Container
          header={
            <Header
              variant="h3"
              actions={
                <Button
                  data-testid="schedule-step-edit"
                  onClick={() => props.onEdit(1)}
                >
                  {t('edit')}
                </Button>
              }
            >
              {t('schedule')}
            </Header>
          }
        >
          <ColumnLayout variant="text-grid" columns={3}>
            <ValueWithLabel label={t('enabled')}>
              {props.schedule.enable ? t('true') : t('false')}
            </ValueWithLabel>
            <ValueWithLabel label={t('schedule')}>
              {props.schedule.enable
                ? t('schedule_value', {
                    workdayStart: props.schedule.workdayStart?.value,
                    workdayEnd: props.schedule.workdayEnd?.value,
                  })
                : '-'}
            </ValueWithLabel>
            <ValueWithLabel label={t('time', { timezone: 'PT' })}>
              {props.schedule.enable ? props.schedule.time?.value : '-'}
            </ValueWithLabel>
          </ColumnLayout>
        </Container>
      )}
    </SpaceBetween>
  );
};
